import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send } from "lucide-react";
import { api } from "@/lib/api";

const TIPOLOGIE = ["Appartamento", "Villa / Villetta", "Attico", "Casa indipendente", "Locale commerciale", "Terreno"];

export default function EvaluationModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    nome: "", telefono: "", email: "", zona: "", tipologia: "Appartamento", mq: "", note: "",
  });
  const [consenso, setConsenso] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono || !form.zona) {
      toast.error("Compila nome, telefono e zona dell'immobile.");
      return;
    }
    if (!consenso) {
      toast.error("Accetta l'informativa privacy per continuare.");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/leads", {
        tipo: "valutazione",
        contatto: { nome: form.nome, telefono: form.telefono, email: form.email || null },
        payload: {
          "Zona / Indirizzo": form.zona,
          Tipologia: form.tipologia,
          Superficie: form.mq ? `${form.mq} mq` : "",
          Note: form.note,
        },
        consensoPrivacy: true,
        provenienza: "Modulo valutazione",
      });
      toast.success("Richiesta inviata! Pierluigi ti ricontatterà a breve.");
      onOpenChange(false);
      setForm({ nome: "", telefono: "", email: "", zona: "", tipologia: "Appartamento", mq: "", note: "" });
      setConsenso(false);
    } catch {
      toast.error("Invio non riuscito, riprova.");
    } finally {
      setSending(false);
    }
  };

  const fieldCls =
    "bg-white border-border-subtle focus-visible:ring-brand/40 focus-visible:border-brand rounded-xl h-12";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-warm border-0 rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="evaluation-modal"
      >
        <div className="bg-brand px-8 py-7 text-white">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-white/70">Prima analisi gratuita</p>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold leading-tight mt-1">
              Scopri quanto può valere
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/80 mt-2">
            Inserisci i dati dell'immobile. Pierluigi analizzerà personalmente la tua richiesta.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-7 space-y-4" data-testid="evaluation-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-anthracite/70">Nome e cognome *</Label>
              <Input className={fieldCls} value={form.nome} onChange={set("nome")} placeholder="Mario Rossi" data-testid="field-nome" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-anthracite/70">Telefono *</Label>
              <Input className={fieldCls} value={form.telefono} onChange={set("telefono")} placeholder="333 000 0000" data-testid="field-telefono" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-anthracite/70">Zona / Indirizzo immobile *</Label>
            <Input className={fieldCls} value={form.zona} onChange={set("zona")} placeholder="Es. Corato, Via ..." data-testid="field-zona" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-anthracite/70">Email (per ricevere conferma)</Label>
            <Input className={fieldCls} type="email" value={form.email} onChange={set("email")} placeholder="tua@email.it" data-testid="field-email" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-anthracite/70">Tipologia</Label>
              <select
                value={form.tipologia}
                onChange={set("tipologia")}
                className={`${fieldCls} w-full px-3 outline-none`}
                data-testid="field-tipologia"
              >
                {TIPOLOGIE.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-anthracite/70">Superficie (mq)</Label>
              <Input className={fieldCls} value={form.mq} onChange={set("mq")} placeholder="Es. 110" inputMode="numeric" data-testid="field-mq" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-anthracite/70">Note (facoltativo)</Label>
            <Textarea
              className="bg-white border-border-subtle focus-visible:ring-brand/40 focus-visible:border-brand rounded-xl min-h-[80px]"
              value={form.note}
              onChange={set("note")}
              placeholder="Stato, ristrutturazioni, spazi esterni..."
              data-testid="field-note"
            />
          </div>

          <label className="flex items-start gap-2 text-xs text-anthracite/60">
            <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)} className="mt-0.5 accent-[#0F5C4A]" data-testid="field-privacy" />
            Acconsento al trattamento dei miei dati secondo l'informativa privacy.
          </label>

          <button
            type="submit"
            disabled={sending}
            className="w-full rounded-full bg-brand text-white font-semibold py-4 flex items-center justify-center gap-2 transition-all hover:bg-brand-hover hover:scale-[1.01] disabled:opacity-50"
            data-testid="evaluation-submit"
          >
            <Send className="h-4 w-4" />
            {sending ? "Invio..." : "Invia la richiesta"}
          </button>
          <p className="text-center text-xs text-anthracite/50">
            Nessun impegno • Valutazione personalizzata • Risposta diretta
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
